"use client";

import { debounce } from "@pharaday/utils";
import { type ReactNode, type Ref, useEffect, useState } from "react";

import { cn } from "../../utils/cn.util";
import { Drawer } from "../drawer";
import { MOBILE_WIDTH, useLayoutContext } from "./context";

export interface LayoutSidebarProps {
  children: ReactNode;
  className?: string;
  ref?: Ref<HTMLDivElement>;
}

const Sidebar = ({ ref, children, className }: LayoutSidebarProps) => {
  const { openSidebar, setOpenSidebar } = useLayoutContext();
  const [windowWidth, setWindowWidth] = useState<number | undefined>();
  useEffect(() => {
    const handleResize = debounce(() => {
      setWindowWidth(window?.innerWidth);
      setOpenSidebar?.(false);
    }, 0);

    window?.addEventListener("resize", handleResize);
    return () => {
      setWindowWidth(undefined);
      window?.removeEventListener("resize", handleResize);
    };
  }, [setOpenSidebar]);

  useEffect(() => {
    setWindowWidth(window?.innerWidth);
  }, []);

  return (
    <>
      {windowWidth &&
        windowWidth > MOBILE_WIDTH && (
          // eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-noninteractive-element-interactions
          <aside
            className={cn(
              "group hidden w-[64px] min-w-[64px] flex-col gap-3 border-x border-neutral-95 bg-white transition-all duration-200 ease-in-out mobile-screen:flex",
              openSidebar && "w-59 min-w-59",
              !openSidebar && "cursor-e-resize",
              className
            )}
            onClick={() => !openSidebar && setOpenSidebar?.(true)}
            ref={ref}
          >
            {children}
          </aside>
        )}
      {windowWidth && windowWidth <= MOBILE_WIDTH && (
        <Drawer.Root
          direction="left"
          open={openSidebar}
          onOpenChange={window?.innerWidth <= MOBILE_WIDTH ? setOpenSidebar : () => null}
        >
          <Drawer.Portal>
            <div className="flex mobile-screen:hidden">
              <Drawer.Overlay className="z-50! bg-black! opacity-60!" />
              <Drawer.Content className="left-0 h-full w-59 min-w-59 flex-col gap-5 border-x border-neutral-95 bg-white">
                {children}
              </Drawer.Content>
            </div>
          </Drawer.Portal>
        </Drawer.Root>
      )}
    </>
  );
};

Sidebar.displayName = "LayoutSidebar";

export default Sidebar;
